import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import type { TextParagraphRun } from '@lite-workspace/dto-fotoclass-templates';
import { paragraphRunHolds } from '@lite-workspace/photobook-model';
import {
  SOFT_LINE_BREAK,
  SlotCursor,
  TEXT_LAYOUT_PARAGRAPHS,
  forcedLines,
  paragraphShapes,
  textLayoutAtLeast,
  wrapParagraphLines,
  zonesCrossFrame,
  type EmptyLeading,
  type HyphenationRules,
  type LineInk,
  type MeasureTextAt,
  type SlotMetrics,
  type WordLanguages,
  type AreaLineSpan,
  type WrapZone,
  type WrappedLine,
} from '@lite-workspace/text-layout';

/**
 * Photoshop paragraphs from `TEXT_LAYOUT_PARAGRAPHS` — mirror of Rust `glyph_layout/paragraphs.rs`. A soft return ends
 * a line inside its paragraph, and every paragraph wraps and places its lines by its own indents, spacing and alignment
 * (`paragraphRuns`, falling back to the box fields). Below that rule the box is one set of attributes, laid out exactly
 * as before: the first line of the BOX takes the first-line indent and the wrap budget ignores it.
 */

/** Mirror of area-shape `MAX_BANDS` / Rust `area_shape::MAX_BANDS`. */
const AREA_MAX_BANDS = 5000;

/** Indents, spacing and alignment one paragraph is laid out by (canvas px). */
export interface ParagraphAttrs {
  readonly firstLineIndent: number;
  readonly startIndent: number;
  readonly endIndent: number;
  readonly spaceBefore: number;
  readonly spaceAfter: number;
  readonly align: RenderTextBox['align'] | undefined;
}

/** What a wrapped line is placed by — mirror of Rust `LineParagraph`. */
export interface LineParagraph {
  readonly attrs: ParagraphAttrs;
  /** The line opens its paragraph (first-line indent). */
  readonly first: boolean;
  /** Paragraph spacing above the line. */
  readonly gapBefore: number;
  readonly justified: boolean;
  /** Indents the justification frame loses on this line. */
  readonly frameReduction: number;
}

function boxAttrs(box: RenderTextBox): ParagraphAttrs {
  return {
    firstLineIndent: box.firstLineIndent ?? 0,
    startIndent: box.startIndent ?? 0,
    endIndent: box.endIndent ?? 0,
    spaceBefore: box.spaceBefore ?? 0,
    spaceAfter: box.spaceAfter ?? 0,
    align: box.align,
  };
}

function withRun(a: ParagraphAttrs, run: TextParagraphRun): ParagraphAttrs {
  return {
    firstLineIndent: run.firstLineIndent ?? a.firstLineIndent,
    startIndent: run.startIndent ?? a.startIndent,
    endIndent: run.endIndent ?? a.endIndent,
    spaceBefore: run.spaceBefore ?? a.spaceBefore,
    spaceAfter: run.spaceAfter ?? a.spaceAfter,
    align: run.align ?? a.align,
  };
}

const justifiedIn = (box: RenderTextBox, a: ParagraphAttrs): boolean => a.align === 'justify' && box.textMode === 'block';

/** Wrap-rule numbers the paragraph wrap needs (mirror of Rust `WrapRule`). */
export interface ParagraphWrapRule {
  readonly slack: number;
  readonly squeezePerGap: number;
}

/** A segment within this of the frame edge sits on that edge: it takes the paragraph's indent on that side. */
const FLUSH_EDGE_PX = 0.5;

function flushEdges(x0: number, width: number, frameW: number): [boolean, boolean] {
  return [x0 < FLUSH_EDGE_PX, frameW - (x0 + width) < FLUSH_EDGE_PX];
}

/** Where the lines of a frame that wraps around objects sit — mirror of Rust `WrapPlan`. */
export class WrapPlan {
  constructor(
    /** Each line's segment of the frame. */
    readonly spans: readonly AreaLineSpan[],
    /** Each line's baseline (baseline space, as `lineBaselines`). */
    readonly baselines: readonly number[],
    private readonly frameW: number,
  ) {}

  /**
   * Indents only apply on the frame edges: a segment beside an object starts and ends at the object, without the
   * paragraph's start, end or first-line indent on that side.
   */
  place(lines: readonly LineParagraph[]): LineParagraph[] {
    return lines.map((lp, i) => {
      const span = this.spans[i];
      const [left, right] = span ? flushEdges(span.x0, span.width, this.frameW) : [true, true];
      const attrs: ParagraphAttrs = {
        ...lp.attrs,
        startIndent: left ? lp.attrs.startIndent : 0,
        firstLineIndent: left ? lp.attrs.firstLineIndent : 0,
        endIndent: right ? lp.attrs.endIndent : 0,
      };
      const frameReduction = attrs.startIndent + attrs.endIndent + (lp.first ? attrs.firstLineIndent : 0);
      return { ...lp, attrs, frameReduction };
    });
  }
}

/**
 * A wrap object crosses the box's frame (`zonesCrossFrame`): the one rule for the engine ({@link TextParagraphs.wrapZones})
 * and the editor — a box whose objects sit outside its frame lays out, aligns and fits as without them.
 */
export function wrapZonesCrossFrame(box: RenderTextBox): boolean {
  return !!box.wrapZones && zonesCrossFrame(box.wrapZones, box.rect.width, box.rect.height);
}

export class TextParagraphs {
  private readonly boxAttrs: ParagraphAttrs;
  /** From `TEXT_LAYOUT_PARAGRAPHS`: each paragraph's first code unit and attributes; `undefined` — the legacy layout. */
  private readonly own: readonly { readonly start: number; readonly attrs: ParagraphAttrs }[] | undefined;

  constructor(
    private readonly box: RenderTextBox,
    text: string,
  ) {
    this.boxAttrs = boxAttrs(box);
    if (!textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS)) {
      this.own = undefined;
      return;
    }

    let start = 0;
    this.own = text.split('\n').map(para => {
      const run = box.paragraphRuns?.find(r => paragraphRunHolds(r, start));
      const entry = { start, attrs: run ? withRun(this.boxAttrs, run) : this.boxAttrs };
      start += para.length + 1;
      return entry;
    });
  }

  /** The box lays out by paragraphs (`TEXT_LAYOUT_PARAGRAPHS`). */
  get byParagraph(): boolean {
    return this.own !== undefined;
  }

  private ownAt(paragraph: number): ParagraphAttrs {
    const own = this.own ?? [];
    return own[Math.min(paragraph, Math.max(0, own.length - 1))]?.attrs ?? this.boxAttrs;
  }

  /** Attributes of the paragraph holding source offset `c`. */
  private atChar(c: number): ParagraphAttrs {
    if (!this.own) return this.boxAttrs;
    let i = 0;
    while (i + 1 < this.own.length && this.own[i + 1].start <= c) i++;
    return this.own[i].attrs;
  }

  /** Some line of the box justifies: the box (legacy) or, by paragraphs, any paragraph. */
  anyJustified(): boolean {
    return this.own ? this.own.some(p => justifiedIn(this.box, p.attrs)) : justifiedIn(this.box, this.boxAttrs);
  }

  /** The alignment the justification gap glyph is looked up under: `justify` when any paragraph asks for it. */
  gapAlign(): RenderTextBox['align'] | undefined {
    return this.own?.some(p => p.attrs.align === 'justify') ? 'justify' : this.boxAttrs.align;
  }

  /** Per wrapped line: its paragraph and how it is placed. */
  lines(wrapped: readonly WrappedLine[]): LineParagraph[] {
    if (!this.own) {
      const a = this.boxAttrs;
      const gap = a.spaceAfter + a.spaceBefore;
      return wrapped.map((_, i) => ({
        attrs: a,
        first: i === 0,
        gapBefore: i > 0 && wrapped[i - 1].paragraphEnd ? gap : 0,
        justified: justifiedIn(this.box, a),
        frameReduction: a.startIndent + a.endIndent,
      }));
    }

    let paragraph = 0;
    // A contour band the text skips is an empty line: the paragraph opens on the first line it really sets, the one
    // the wrap gave the first-line budget (`wrap`).
    let opening = false;
    return wrapped.map((w, i) => {
      const opens = i === 0 || wrapped[i - 1].paragraphEnd;
      let gapBefore = 0;
      if (i > 0 && opens) {
        gapBefore = this.ownAt(paragraph).spaceAfter;
        paragraph += 1;
        gapBefore += this.ownAt(paragraph).spaceBefore;
      }

      const deadBand = w.text === '' && !w.paragraphEnd && !w.lineForced;
      opening ||= opens;
      const first = opening && !deadBand;
      opening &&= deadBand;
      const attrs = this.ownAt(paragraph);
      const frameReduction = attrs.startIndent + attrs.endIndent + (first ? attrs.firstLineIndent : 0);
      return { attrs, first, gapBefore, justified: justifiedIn(this.box, attrs), frameReduction };
    });
  }

  /** Wrap budget of line `i` of `paragraph` (contour band, else the frame), less the paragraph's indents. */
  private widthAt(
    i: number,
    paragraph: number,
    first: boolean,
    rule: ParagraphWrapRule,
    areaSpans: readonly (AreaLineSpan | null)[] | undefined,
  ): number {
    const a = this.ownAt(paragraph);
    const reduction = a.startIndent + a.endIndent + (first ? a.firstLineIndent : 0);
    if (areaSpans !== undefined && i < areaSpans.length) {
      const span = areaSpans[i];
      return span ? span.width + rule.slack - reduction : 0;
    }

    return this.box.rect.width + rule.slack - reduction;
  }

  /** Wrap budget of each laid-out line — what a line wider than it spills over (frame fit). */
  budgetOf(
    lines: readonly LineParagraph[],
    rule: ParagraphWrapRule,
    areaSpans: readonly (AreaLineSpan | null)[] | undefined,
  ): (i: number) => number {
    return i => {
      const lp = lines[i];
      if (!lp) return this.box.rect.width + rule.slack;
      if (areaSpans !== undefined && i < areaSpans.length) {
        const span = areaSpans[i];
        return span ? span.width + rule.slack - lp.frameReduction : 0;
      }

      return this.box.rect.width + rule.slack - lp.frameReduction;
    };
  }

  /**
   * Wrap by paragraphs — mirror of Rust `Paragraphs::wrap`: each paragraph to its own frame (the first line loses the
   * first-line indent too), a soft return a forced line inside its paragraph, a candidate measured where it stands.
   * Point text keeps its forced lines. Only for {@link byParagraph}.
   */
  wrap(
    text: string,
    measure: MeasureTextAt,
    isGap: (ch: string) => boolean,
    rule: ParagraphWrapRule,
    areaSpans: readonly (AreaLineSpan | null)[] | undefined,
    hyphenation: HyphenationRules | undefined,
    languages: WordLanguages | undefined,
  ): WrappedLine[] {
    const measureBreak = this.measureBreak(measure, isGap, rule);
    const widthAt = (i: number, paragraph: number, first: boolean): number => this.widthAt(i, paragraph, first, rule, areaSpans);
    const block = this.box.textMode === 'block';
    let usable = areaSpans === undefined;
    for (let i = 0; !usable && i < AREA_MAX_BANDS; i++) usable = widthAt(i, 0, false) > 0;
    const widths =
      block && usable && (this.own?.length ?? 0) > 0 ? { widthAt, skipsDeadBands: areaSpans !== undefined } : undefined;
    return wrapParagraphLines(forcedLines(text), measureBreak, widths, hyphenation, languages);
  }

  /** A justified paragraph's candidate may close its spaces to stay on its line (`WrapRule.squeezePerGap`). */
  private measureBreak(measure: MeasureTextAt, isGap: (ch: string) => boolean, rule: ParagraphWrapRule): MeasureTextAt {
    return (line, start) => {
      const w = measure(line, start);
      if (rule.squeezePerGap === 0 || !justifiedIn(this.box, this.atChar(start))) return w;
      let gaps = 0;
      for (const ch of line) if (isGap(ch)) gaps += 1;
      return w - rule.squeezePerGap * gaps;
    };
  }

  /**
   * The frame wraps around `wrapZones` (`wrap-slots.ts`): only a block of {@link byParagraph} without an area contour
   * or a path, and only when a zone crosses the frame (`zonesCrossFrame`); any other box keeps its layout. Mirror of
   * Rust `Paragraphs::wrap_zones`.
   */
  wrapZones(areaContour: boolean): readonly WrapZone[] | undefined {
    const zones = this.box.wrapZones;
    const fits = this.box.textMode === 'block' && !areaContour && !this.box.textPath && this.byParagraph;
    return zones && fits && wrapZonesCrossFrame(this.box) ? zones : undefined;
  }

  /**
   * {@link wrap} around page objects: every line gets the next slot of the frame (a baseline beside an object holds one
   * per side), its width less the indents of the frame edges it touches; it steps from the baseline above by
   * `leadingAt` and meets the zones by `inkAt` of its index and the source offset it starts at — an empty line by
   * `leadingAt` of the line break that ends it. Mirror of Rust `Paragraphs::wrap_around`.
   */
  wrapAround(
    text: string,
    measure: MeasureTextAt,
    isGap: (ch: string) => boolean,
    rule: ParagraphWrapRule,
    zones: readonly WrapZone[],
    metrics: SlotMetrics,
    leadingAt: (i: number, start: number) => number,
    inkAt: (i: number, start: number) => LineInk,
    hyphenation: HyphenationRules | undefined,
    languages: WordLanguages | undefined,
  ): { wrapped: WrappedLine[]; plan: WrapPlan } {
    const measureBreak = this.measureBreak(measure, isGap, rule);
    const shapes = paragraphShapes(text, SOFT_LINE_BREAK, p => this.ownAt(p - 1).spaceAfter + this.ownAt(p).spaceBefore);
    const cursor = new SlotCursor(zones, metrics, shapes);
    // The first word of the line about to be asked for: its break width (designcraft-compose `holds`) and the source
    // offset it starts at.
    let need = 0;
    let needStart = 0;
    const lineStart = (word: number, start: number): void => {
      need = word;
      needStart = start;
    };

    // An empty line starts at the line break that ends it, so the empty lines before line `i` start at the breaks right
    // before `opens` (the end of the text past its last empty line).
    const emptyBefore =
      (i: number, opens: number): EmptyLeading =>
      j =>
        leadingAt(j, Math.max(opens + j - i, 0));

    const widthAt = (i: number, paragraph: number, first: boolean): number => {
      const a = this.ownAt(paragraph);
      const firstIndent = first ? a.firstLineIndent : 0;
      const room = (x0: number, width: number): number => {
        const [left, right] = flushEdges(x0, width, metrics.frameW);
        const reduction = (left ? a.startIndent + firstIndent : 0) + (right ? a.endIndent : 0);
        return width + rule.slack - reduction;
      };

      // A word wider than the whole frame holds nowhere: it breaks in the first segment, as the frame breaks it.
      const word = need > metrics.frameW + rule.slack - a.startIndent - a.endIndent - firstIndent ? 0 : need;
      const opens = Math.max(text.lastIndexOf('\n', needStart - 1), text.lastIndexOf(SOFT_LINE_BREAK, needStart - 1)) + 1;
      const holds = (x0: number, x1: number): boolean => room(x0, x1 - x0) >= word;
      const s = cursor.slot(i, paragraph, first, leadingAt(i, needStart), inkAt(i, needStart), emptyBefore(i, opens), holds);
      return room(s.x0, s.width);
    };

    const wrapped = wrapParagraphLines(
      forcedLines(text),
      measureBreak,
      { widthAt, skipsDeadBands: false, lineStart },
      hyphenation,
      languages,
    );
    const slots = cursor.finish(wrapped.length, emptyBefore(wrapped.length, text.length + 1));
    const plan = new WrapPlan(
      slots.map(s => ({ x0: s.x0, width: s.width })),
      slots.map(s => s.baseline),
      metrics.frameW,
    );
    return { wrapped, plan };
  }
}
